/**
 * Vellum overlay shell — a native `<dialog>` shown with `showModal()`, shared app-wide: the
 * result overlays and trick review, the pause menu, the profile overlay (the identity overlay is
 * a column inside it), the games and leaderboard overlays, the five reference pages, the
 * multiplayer create overlay, waiting-state.js's release overlay, and confirm-dialog.js. The
 * platform supplies what used to be hand-rolled: focus trap, focus restore, Escape, background
 * inertness, top-layer stacking. One open overlay at a time: opening dismisses any prior overlay,
 * so overlay-from-overlay (the breakdown's Show-hands affordance) just works.
 * Leaf module: an overlay's content stays with it; the close affordance does not — the corner
 * close × is shell-owned and mounted on every overlay by default (see `closeButton`).
 */
import { t } from "./i18n.js";
import {
  createPateeChevron,
  createSwallowtailSaltire,
} from "./icons/prompt-icons.js";
import { div, span } from "./utils.js";

// The one open overlay's dismiss, so a fresh open can tear the prior one down through one path —
// null exactly when nothing is open. Enforces "one overlay at a time" (see file header).
let closeCurrentOverlay = null;

/** True while any overlay is open — the read-only sibling of the latch above, for a caller that
 *  must never supersede (a timer-fired overlay, e.g. waiting-state.js's release overlay, which
 *  must not tear down a pause menu or help page the player happens to be reading; every other
 *  opener is user-initiated and relies on openVellumOverlay's unconditional supersede instead). */
export function isOverlayOpen() {
  return closeCurrentOverlay != null;
}

// Belt-and-suspenders cap for the exit-animation path: if animationend never
// fires (a 0-duration animation under reduced-motion doesn't reliably emit it),
// teardown still runs by this deadline. Comfortably past --transition-normal (200ms).
const EXIT_ANIM_FALLBACK_MS = 350;

// Per-instance unique title ids (the same shape as confirm-dialog.js's `dialogIdCounter`) — two
// open overlays (an overlay-from-overlay chain) must not collide on aria-labelledby targets.
let overlayTitleIdCounter = 0;

/** Opens an empty modal `<dialog class="overlay-panel …">`. Returns the panel (append content to
 *  it), the dismiss function (backdrop click, Escape, and the corner × are already wired), and
 *  `setBack` (see below).
 *
 *  `role` (default the native `dialog`) and `ariaLabel` are opt-in — an overlay that mounts a
 *  labelled title may instead set `aria-labelledby` on the returned panel, which wins per ARIA
 *  precedence. `onClose` fires exactly once, on every close path (dismiss / Escape / outside-click
 *  / supersede) — four consumers: confirm-dialog.js resolves its promise from it, the profile
 *  overlay flips the guard its submit-time resolve reads, the result progression overlay
 *  disconnects its observer, and waiting-state.js drops its dismiss handle.
 *
 *  `closeButton` (default on) mounts the corner close × — the one close affordance, on every
 *  overlay, replacing the bottom Close row each overlay used to append for itself. Exactly two
 *  callers opt out with `false`, both because they already carry their own close semantics in
 *  their actions: confirm-dialog.js (Cancel is its "no", under `role: "alertdialog"`) and
 *  waiting-state.js's release overlay (its own CTA). Everyone else takes the default — the point
 *  of retiring the row was one dismiss vocabulary, not two (DESIGN 8/12).
 *
 *  `header` opts this overlay into the shell-owned header vocabulary: `{ icon?, title, subtitle? }`.
 *  `icon` is a prebuilt DOM element — callers own their icon-factory imports, so a caller's icon
 *  family never reaches this module (the two marks it draws itself, § the chrome pair below,
 *  are the only icons it imports) — mounted in the icon slot; absent (`null`/`undefined`) renders no slot. `title` is an
 *  already-translated string, rendered as `.overlay-title` with a generated id and set as the
 *  dialog's `aria-labelledby` (adopting callers drop the now-redundant `ariaLabel`). `subtitle` is
 *  an optional string. Reserved deliberately: four overlays stay header-less — the pause menu
 *  (icon-only heritage heading), the profile overlay (its mode-dependent identity headline is
 *  content, not chrome — the Knight persona already leads it), confirm-dialog.js (its own
 *  `aria-labelledby`/`-describedby` + action semantics), and waiting-state.js's release overlay
 *  (which self-wires its own `aria-labelledby`, the pattern below). The selectivity is the point
 *  (DESIGN 12/17: a vocabulary earns meaning by being reserved).
 *
 *  `setBack(onBack)` — returned alongside `panel`/`dismiss` — mounts a back chevron as the first
 *  child of the header's title row; `setBack(null)` removes it, and repeat calls replace rather
 *  than duplicate. Only meaningful with a `header`; without one `setBack` is `null` — header-less
 *  overlays never call it.
 *
 *  `exitAnim` opts this overlay into a symmetric close: the caller supplies a panel-out
 *  `@keyframes` (keyed off the `overlay-panel--out` marker the dismiss adds), and teardown waits
 *  for it to finish — so the overlay fades out instead of hard-cutting (DESIGN 14). Off by
 *  default: the other overlays keep their instant close, so this is a zero-behaviour-change
 *  addition for them. A supersede (a fresh overlay opening) always tears down immediately,
 *  animation or not — you don't leave the outgoing overlay fading while the next one opens. */
export function openVellumOverlay(
  panelClass = "",
  {
    exitAnim = false,
    closeButton = true,
    role,
    ariaLabel,
    header,
    onClose,
  } = {},
) {
  // Supersede first — load-bearing order: closing the outgoing dialog lets the platform restore
  // focus to ITS opener before the incoming showModal() captures that element, so an
  // overlay-from-overlay chain (breakdown → hands) hands focus back correctly all the way up.
  closeCurrentOverlay?.();

  const panel = document.createElement("dialog");
  panel.className = `overlay-panel${panelClass ? ` ${panelClass}` : ""}`;
  if (role != null) panel.setAttribute("role", role);
  if (ariaLabel != null) panel.setAttribute("aria-label", ariaLabel);

  const { teardown, dismiss } = wireOverlayClose(panel, exitAnim, onClose);
  closeCurrentOverlay = teardown; // supersede is always immediate, animation or not

  // Ahead of the header (and of the caller's content): the anchor must be the panel's first
  // flex child for its gap-cancelling negative margin to leave the layout untouched.
  if (closeButton) mountCloseButton(panel, dismiss);
  const setBack = header != null ? mountHeader(panel, header) : null;

  document.body.appendChild(panel);
  panel.showModal();
  return { panel, dismiss, setBack };
}

/** Builds the shell-owned header (icon · title row · subtitle), appends it to `panel` ahead of the
 *  caller's own content, wires `aria-labelledby`, and returns this open's `setBack`. */
function mountHeader(panel, { icon, title, subtitle }) {
  const headerEl = div("overlay-header");
  const titleRow = div("overlay-title-row");
  if (icon != null) {
    const iconWrap = div("overlay-header-icon");
    iconWrap.appendChild(icon);
    titleRow.appendChild(iconWrap);
  }
  const titleEl = span(title, "overlay-title");
  titleEl.id = `overlay-title-${++overlayTitleIdCounter}`;
  titleRow.appendChild(titleEl);
  headerEl.appendChild(titleRow);
  if (subtitle != null) {
    const subtitleEl = div("overlay-subtitle");
    subtitleEl.textContent = subtitle;
    headerEl.appendChild(subtitleEl);
  }
  panel.appendChild(headerEl);
  panel.setAttribute("aria-labelledby", titleEl.id);

  let backBtn = null;
  return function setBack(onBack) {
    backBtn?.remove();
    backBtn = null;
    if (onBack == null) return;
    backBtn = buildBackButton(onBack);
    titleRow.prepend(backBtn);
  };
}

/** The one back affordance (§2): a quiet square hit-target leading the title row, carrying the
 *  patée chevron — the close saltire's own pair, same filled pen (overlay-close-heritage-x-icon,
 *  2026-09-05; docs/components/MARKS.md § The overlay close mark and its chevron). It replaced a
 *  typographic `‹`, which a bare `<button>` painted in Arial, inheriting none of the app's font:
 *  a drawn × beside a browser-drawn chevron reads as two registers, not one hand. Drawn, not
 *  material — no etch, no plate (DESIGN 17/18: chrome recedes, and the etch is reserved for a
 *  significant first arrival). No size argument: CSS owns the rendered size, the family pattern
 *  `menu-button.js` and `fullscreen-button.js` follow, and `currentColor` lets the button's own
 *  quiet vellum carry it. Back navigates (reopening the parent overlay); close/Escape/backdrop
 *  still dismiss — native `<dialog>` semantics untouched. */
function buildBackButton(onBack) {
  const el = document.createElement("button");
  el.className = "overlay-back-btn";
  el.appendChild(createPateeChevron());
  el.setAttribute("aria-label", t("overlayBack"));
  el.addEventListener("click", onBack);
  return el;
}

/** The one close affordance: the swallowtail saltire in the panel's top-right corner, the back
 *  chevron's own register and its own pen — the × everyone knows re-cut in the heritage
 *  vocabulary, the move `createMenuIcon` made with the hamburger. Drawn, not material: no etch,
 *  no plate (DESIGN 17/18 — chrome recedes, and the etch entrance is reserved for a significant
 *  first arrival, which a control on every overlay is the opposite of). The quiet square hit
 *  target is unchanged; `currentColor` is what lets the button's vellum recolor it on hover.
 *  No size argument — CSS owns the rendered size (`menu-button.js` / `fullscreen-button.js`'s
 *  pattern). It rides a zero-height sticky anchor rather than sitting in the flow, so it costs the panel no layout
 *  height and holds the corner while the panel scrolls (skat.css § Result Overlays carries the
 *  geometry). Clicking it calls `dismiss` — the same path Escape and the backdrop take, so
 *  `exitAnim` and the exactly-once `onClose` behave identically on all four. */
function mountCloseButton(panel, dismiss) {
  const el = document.createElement("button");
  el.className = "overlay-close-btn";
  el.appendChild(createSwallowtailSaltire());
  el.setAttribute("aria-label", t("overlayClose"));
  el.addEventListener("click", dismiss);
  const anchor = div("overlay-close-anchor");
  anchor.appendChild(el);
  panel.appendChild(anchor);
}

/** Wires one overlay's close: an idempotent `teardown` (closes + removes the dialog, fires
 *  `onClose`) and the `dismiss` the caller, outside-click, and Escape share. Without `exitAnim`,
 *  dismiss IS teardown (instant); with it, dismiss plays the exit animation and lets animationend
 *  (or a fallback timer) call teardown — the symmetric close. The supersede path takes `teardown`
 *  directly, so a fresh overlay never waits on the outgoing one's animation. */
function wireOverlayClose(panel, exitAnim, onClose) {
  let closed = false;
  const teardown = () => {
    if (closed) return;
    closed = true;
    panel.close();
    panel.remove();
    if (closeCurrentOverlay === teardown) closeCurrentOverlay = null;
    onClose?.();
  };
  const dismiss = exitAnim ? buildAnimatedDismiss(panel, teardown) : teardown;
  // Escape arrives as the dialog's native `cancel` event — preventDefault so the platform's
  // default (an unconditional close()) doesn't race our own teardown/exit-animation path.
  panel.addEventListener("cancel", (e) => {
    e.preventDefault();
    dismiss();
  });
  // A `::backdrop` click bubbles to the dialog itself — there's no separate backdrop element to
  // listen on anymore, so outsideClick tells a backdrop click from one on the panel's own content.
  panel.addEventListener("click", (e) => {
    if (outsideClick(panel, e)) dismiss();
  });
  return { teardown, dismiss };
}

/** The exit-anim dismiss: adds the `--out` marker (re-entry-guarded) and defers `teardown` to
 *  `animationend` or the fallback timer, instead of running it immediately. */
function buildAnimatedDismiss(panel, teardown) {
  return () => {
    if (panel.classList.contains("overlay-panel--out")) return;
    panel.classList.add("overlay-panel--out");
    panel.addEventListener("animationend", teardown, { once: true });
    setTimeout(teardown, EXIT_ANIM_FALLBACK_MS);
  };
}

/** True when a click on the dialog landed outside its own box (i.e. hit the `::backdrop`). Replaces
 *  the old backdrop-click listener + the panel's stopPropagation guard with one hit-test. */
function outsideClick(panel, e) {
  const r = panel.getBoundingClientRect();
  return (
    e.clientX < r.left ||
    e.clientX > r.right ||
    e.clientY < r.top ||
    e.clientY > r.bottom
  );
}
